import { useState, useCallback } from 'react';
import { Check, FlaskConical, Grid, AlertTriangle } from 'lucide-react';
import LabBench from '../../LabBench';
import InfoBox from '../../InfoBox';

const stepInfoContent: Record<number, { title: string; content: string; whatHappens?: string; observe?: string[]; equation?: string }> = {
  1: {
    title: 'Laboratory Safety',
    content: 'This experiment involves ionic solutions, some of which contain heavy metals (Ag⁺, Cu²⁺) that are toxic and require special disposal. Sulfide solutions produce hydrogen sulfide gas with a characteristic "rotten egg" odor.',
    whatHappens: 'You review safety protocols before beginning the experiment. All heavy metal waste must go in designated containers.',
    observe: ['Wear gloves throughout', 'Work in ventilated area for sulfide tests', 'Never dispose of heavy metals down drain', 'Some solutions are corrosive']
  },
  2: {
    title: 'Preparing the Testing Grid',
    content: 'A systematic grid allows us to test 11 cations with 8 anions, giving 88 combinations. This matrix approach is efficient for determining solubility patterns and deriving general rules.',
    whatHappens: 'You set up an 11x8 grid with cations as columns and anions as rows, allowing systematic testing of all combinations.',
    observe: ['11 cations: Al³⁺, Ba²⁺, Ca²⁺, Co²⁺, Cu²⁺, Fe³⁺, Mg²⁺, Mn²⁺, Ni²⁺, Ag⁺, Zn²⁺', '8 anions: Cl⁻, Br⁻, I⁻, S²⁻, SO₄²⁻, CO₃²⁻, PO₄³⁻, OH⁻', '88 total combinations to test']
  },
  3: {
    title: 'Placing the Acetate Sheet',
    content: 'The clear acetate sheet provides a non-reactive, transparent surface for mixing small drops of solutions. It allows easy observation of precipitates while keeping the paper grid visible underneath.',
    whatHappens: 'The acetate sheet is placed over the labeled grid, creating individual testing wells at each intersection.',
    observe: ['Acetate prevents solutions from soaking paper', 'Grid labels remain visible', 'Easy to see color changes against white background']
  }
};

export default function Part1Setup() {
  const [showInfo, setShowInfo] = useState<number | null>(null);
  const [safetyAcknowledged, setSafetyAcknowledged] = useState(false);
  const [gridPrepared, setGridPrepared] = useState(false);
  const [acetatePlaced, setAcetatePlaced] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);

  const completedSteps = [safetyAcknowledged, gridPrepared, acetatePlaced].filter(Boolean).length;

  const handleSafetyAcknowledge = useCallback(() => {
    if (!safetyAcknowledged) {
      setSafetyAcknowledged(true);
      setCurrentStep(2);
    }
  }, [safetyAcknowledged]);

  const handlePrepareGrid = useCallback(() => {
    if (safetyAcknowledged && !gridPrepared) {
      setGridPrepared(true);
      setCurrentStep(3);
    }
  }, [safetyAcknowledged, gridPrepared]);

  const handlePlaceAcetate = useCallback(() => {
    if (gridPrepared && !acetatePlaced) {
      setAcetatePlaced(true);
    }
  }, [gridPrepared, acetatePlaced]);

  const cations = ['Al3+', 'Ba2+', 'Ca2+', 'Co2+', 'Cu2+', 'Fe3+', 'Mg2+', 'Mn2+', 'Ni2+', 'Ag+', 'Zn2+'];
  const anions = ['Cl-', 'Br-', 'I-', 'S2-', 'SO42-', 'CO32-', 'PO43-', 'OH-'];

  return (
    <div className="experiment-layout">
      <div className="steps-panel">
        <div className="steps-header">
          <FlaskConical size={16} />
          <h3>Part I: Setup & Introduction</h3>
        </div>
        <div className="steps-list">
          {[
            { id: 1, title: 'Safety Briefing', hint: 'Review safety procedures', done: safetyAcknowledged },
            { id: 2, title: 'Prepare Grid', hint: 'Set up 11x8 testing grid', done: gridPrepared },
            { id: 3, title: 'Place Acetate', hint: 'Cover grid with acetate sheet', done: acetatePlaced }
          ].map((step) => (
            <div
              key={step.id}
              className={`step-item ${step.done ? 'completed' : ''} ${currentStep === step.id ? 'active' : ''}`}
            >
              <div className={`step-number ${step.done ? 'done' : ''}`}>
                {step.done ? <Check size={12} /> : step.id}
              </div>
              <div className="step-content">
                <span className="step-title">{step.title}</span>
                {currentStep === step.id && !step.done && (
                  <span className="step-hint">{step.hint}</span>
                )}
              </div>
              {stepInfoContent[step.id] && (
                <button
                  className="step-info-btn"
                  onClick={(e) => { e.stopPropagation(); setShowInfo(step.id); }}
                >
                  ?
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Ion Lists */}
        <div className="ion-lists">
          <div className="ion-group">
            <h4>11 Cations (columns)</h4>
            <div className="ion-tags">
              {cations.map(c => (
                <span key={c} className="ion-tag cation">{c}</span>
              ))}
            </div>
          </div>
          <div className="ion-group">
            <h4>8 Anions (rows)</h4>
            <div className="ion-tags">
              {anions.map(a => (
                <span key={a} className="ion-tag anion">{a}</span>
              ))}
            </div>
          </div>
        </div>
      </div>

      <LabBench
        experimentName="Salt Solubility Setup"
        completedCount={completedSteps}
        totalSteps={3}
        equipment={['Grid paper', 'Acetate sheet', 'Marker']}
        labMode="guided"
        currentStep={currentStep}
      >
        <div className="setup-workspace">
          {/* Safety Panel */}
          {currentStep === 1 && !safetyAcknowledged && (
            <div className="safety-panel" onClick={handleSafetyAcknowledge}>
              <AlertTriangle size={48} className="safety-icon" />
              <h3>Safety Review</h3>
              <ul className="safety-list">
                <li>Some solutions are mildly corrosive - wear gloves</li>
                <li>Heavy metal solutions (Ag<sup>+</sup>, Cu<sup>2+</sup>) require special disposal</li>
                <li>Sodium sulfide (Na<sub>2</sub>S) has a characteristic odor</li>
                <li>Work in a well-ventilated area</li>
                <li>Do not dispose of heavy metals down the drain</li>
              </ul>
              <button className="acknowledge-btn">I Understand - Continue</button>
            </div>
          )}

          {/* Grid Preparation */}
          {currentStep === 2 && safetyAcknowledged && !gridPrepared && (
            <div className="grid-prep" onClick={handlePrepareGrid}>
              <Grid size={48} className="grid-icon" />
              <h3>Prepare the 11 x 8 Grid</h3>
              <p>Create a grid with cations as columns and anions as rows.</p>
              <div className="mini-grid-preview">
                <div className="grid-preview">
                  <div className="corner"></div>
                  {cations.slice(0, 5).map(c => (
                    <div key={c} className="header cation">{c.substring(0, 2)}</div>
                  ))}
                  <div className="header more">...</div>
                  {anions.slice(0, 3).map(a => (
                    <div key={a} className="row-header">{a}</div>
                  ))}
                  {[...Array(3 * 6)].map((_, i) => (
                    <div key={i} className="cell"></div>
                  ))}
                </div>
              </div>
              <button className="prep-btn">Set Up Grid</button>
            </div>
          )}

          {/* Acetate Placement */}
          {currentStep === 3 && gridPrepared && !acetatePlaced && (
            <div className="acetate-step" onClick={handlePlaceAcetate}>
              <h3>Place Acetate Sheet</h3>
              <p>The clear acetate sheet goes over the grid paper to contain the drops.</p>
              <div className="acetate-visual">
                <div className="grid-base">
                  <div className="acetate-sheet">
                    <span>Click to place acetate</span>
                  </div>
                </div>
              </div>
              <button className="place-btn">Place Acetate Sheet</button>
            </div>
          )}

          {/* Setup Complete */}
          {acetatePlaced && (
            <div className="setup-complete">
              <Check size={48} className="complete-icon" />
              <h3>Setup Complete!</h3>
              <p>Your testing grid is ready. You will now test each anion with all 11 cations.</p>
              <div className="next-steps">
                <h4>Testing Order:</h4>
                <ol>
                  <li>Chloride (Cl<sup>-</sup>)</li>
                  <li>Bromide (Br<sup>-</sup>)</li>
                  <li>Iodide (I<sup>-</sup>)</li>
                  <li>Sulfide (S<sup>2-</sup>)</li>
                  <li>Sulfate (SO<sub>4</sub><sup>2-</sup>)</li>
                  <li>Carbonate (CO<sub>3</sub><sup>2-</sup>)</li>
                  <li>Phosphate (PO<sub>4</sub><sup>3-</sup>)</li>
                  <li>Hydroxide (OH<sup>-</sup>)</li>
                </ol>
              </div>
            </div>
          )}

          {/* Theory Box */}
          <div className="theory-box">
            <h4>Salt Solubility Principles</h4>
            <p>
              When two ionic solutions are mixed, a precipitate forms if the product
              of ion concentrations exceeds the K<sub>sp</sub> (solubility product).
              We will observe which combinations produce precipitates and derive
              general solubility rules.
            </p>
          </div>
        </div>

        <style>{`
          .setup-workspace {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 24px;
            padding: 20px;
            width: 100%;
          }

          .safety-panel {
            background: var(--bg-elevated);
            border: 2px solid #F59E0B;
            border-radius: 12px;
            padding: 24px;
            text-align: center;
            max-width: 450px;
            cursor: pointer;
            transition: all 0.2s;
          }

          .safety-panel:hover {
            border-color: var(--secondary);
          }

          .safety-icon {
            color: #F59E0B;
            margin-bottom: 12px;
          }

          .safety-list {
            text-align: left;
            margin: 0 0 20px 0;
            padding-left: 20px;
          }

          .safety-list li {
            margin: 8px 0;
            color: var(--text-secondary);
            font-size: 14px;
          }

          .acknowledge-btn, .prep-btn, .place-btn {
            background: var(--secondary);
            color: white;
            border: none;
            padding: 10px 24px;
            border-radius: 6px;
            font-weight: 600;
            cursor: pointer;
          }

          .grid-prep, .acetate-step {
            background: var(--bg-elevated);
            border: 2px solid var(--border);
            border-radius: 12px;
            padding: 24px;
            text-align: center;
            max-width: 450px;
            cursor: pointer;
            transition: all 0.2s;
          }

          .grid-prep:hover, .acetate-step:hover {
            border-color: var(--secondary);
          }

          .grid-icon {
            color: var(--secondary);
            margin-bottom: 12px;
          }

          .grid-prep h3, .acetate-step h3 {
            margin: 0 0 8px 0;
          }

          .grid-prep p, .acetate-step p {
            margin: 0 0 16px 0;
            color: var(--text-secondary);
            font-size: 14px;
          }

          .mini-grid-preview {
            margin: 20px 0;
          }

          .grid-preview {
            display: grid;
            grid-template-columns: 40px repeat(6, 30px);
            gap: 2px;
            background: var(--bg-surface);
            padding: 8px;
            border-radius: 4px;
            width: fit-content;
            margin: 0 auto;
          }

          .grid-preview .corner {
            background: var(--bg-elevated);
          }

          .grid-preview .header {
            background: rgba(0, 168, 107, 0.2);
            font-size: 9px;
            padding: 4px;
            color: var(--secondary);
            font-weight: 600;
          }

          .grid-preview .row-header {
            background: rgba(59, 130, 246, 0.2);
            font-size: 9px;
            padding: 4px;
            color: #60A5FA;
            font-weight: 600;
          }

          .grid-preview .cell {
            background: var(--bg-elevated);
            height: 20px;
          }

          .grid-preview .more {
            font-size: 8px;
          }

          .acetate-visual {
            margin: 20px 0;
          }

          .grid-base {
            width: 200px;
            height: 120px;
            background: repeating-linear-gradient(
              0deg,
              transparent,
              transparent 19px,
              var(--border) 19px,
              var(--border) 20px
            ),
            repeating-linear-gradient(
              90deg,
              transparent,
              transparent 19px,
              var(--border) 19px,
              var(--border) 20px
            );
            background-color: #FFFDF7;
            border-radius: 4px;
            position: relative;
            margin: 0 auto;
          }

          .acetate-sheet {
            position: absolute;
            top: 5px;
            left: 5px;
            right: 5px;
            bottom: 5px;
            background: rgba(200, 220, 240, 0.3);
            border: 2px dashed #7A9BB8;
            border-radius: 2px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 12px;
            color: #5A7A95;
          }

          .setup-complete {
            background: var(--bg-elevated);
            border: 2px solid var(--secondary);
            border-radius: 12px;
            padding: 24px;
            text-align: center;
            max-width: 400px;
          }

          .complete-icon {
            color: var(--secondary);
            margin-bottom: 12px;
          }

          .setup-complete h3 {
            margin: 0 0 8px 0;
            color: var(--secondary);
          }

          .setup-complete p {
            margin: 0 0 16px 0;
            color: var(--text-secondary);
          }

          .next-steps {
            text-align: left;
            background: var(--bg-surface);
            padding: 12px;
            border-radius: 8px;
          }

          .next-steps h4 {
            margin: 0 0 8px 0;
            font-size: 13px;
            color: var(--text-primary);
          }

          .next-steps ol {
            margin: 0;
            padding-left: 20px;
            font-size: 12px;
            color: var(--text-secondary);
          }

          .next-steps li {
            margin: 4px 0;
          }

          .ion-lists {
            background: var(--bg-elevated);
            border-radius: 8px;
            padding: 12px;
            margin-top: 16px;
          }

          .ion-group {
            margin-bottom: 12px;
          }

          .ion-group:last-child {
            margin-bottom: 0;
          }

          .ion-group h4 {
            margin: 0 0 8px 0;
            font-size: 11px;
            color: var(--text-secondary);
          }

          .ion-tags {
            display: flex;
            flex-wrap: wrap;
            gap: 4px;
          }

          .ion-tag {
            font-size: 9px;
            padding: 2px 5px;
            border-radius: 3px;
          }

          .ion-tag.cation {
            background: rgba(0, 168, 107, 0.2);
            color: var(--secondary);
          }

          .ion-tag.anion {
            background: rgba(59, 130, 246, 0.2);
            color: #60A5FA;
          }

          .theory-box {
            background: var(--bg-elevated);
            border-radius: 8px;
            padding: 16px;
            max-width: 500px;
            border-left: 3px solid var(--secondary);
          }

          .theory-box h4 {
            margin: 0 0 8px 0;
            color: var(--secondary);
            font-size: 14px;
          }

          .theory-box p {
            margin: 0;
            font-size: 13px;
            color: var(--text-secondary);
            line-height: 1.5;
          }
        .step-info-btn {
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: var(--accent);
          color: var(--bg-dark);
          font-size: 11px;
          font-weight: 700;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          transition: transform 0.15s;
        }
        .step-info-btn:hover {
          transform: scale(1.1);
        }
        `}</style>
      </LabBench>

      {showInfo && stepInfoContent[showInfo] && (
        <InfoBox
          step={showInfo}
          experimentId="exp7"
          stepInfo={stepInfoContent[showInfo]}
          onClose={() => setShowInfo(null)}
        />
      )}
    </div>
  );
}
